import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  FileOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { ConfigProvider, Spin, Table } from 'antd';
import { Button, Input, Tooltip } from '@mm/components';

import css from './SetupForm.module.css';
import { useSelector } from 'react-redux';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { useGoogleAdsCRUDActions } from '@mm/state/actions/GoogleAdsCRUDActions';
import { selectGoogleClientCredentials } from '@mm/state/selectors/googleAdsSelectors';
import { errorToast } from '../../../../../../common/helpers/toast';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useViewActions } from '@mm/state/actions/viewActions';

type SetupFormProps = {
  className?: string;
};

const AccountsStep = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const googleAdsCRUDActions = useGoogleAdsCRUDActions();

  const [busy, setBusy] = useState(false);

  const modalActions = useModalActions();

  const credentials = useSelector(selectGoogleClientCredentials);

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [customerId, setCustomerId] = useState('');

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Client ID',
      dataIndex: 'clientId',
      key: 'clientId',
    },
    {
      title: 'Secret Token',
      dataIndex: 'clientSecret',
      key: 'clientSecret',
    },
    {
      title: 'Customer ID',
      dataIndex: 'customerId',
      key: 'customerId',
    },
    {
      title: 'Refresh Token',
      dataIndex: 'refreshToken',
      key: 'refreshToken',
    },
    {
      title: 'User',
      dataIndex: 'user.email',
      key: 'email',
      render: (text, record) => <span>{record.user?.email}</span>,
    },
    {
      title: '',
      dataIndex: '',
      key: 'delete',
      render: (text, record) => (
        <Button
          color="secondary"
          onClick={() => {
            modalActions.openGeneralConfirmModal({
              headerText: 'Are you sure?',
              messageText: 'This action can not be undone',
              onCloseHandler: async ok => {
                if (ok) {
                  await googleAdsCRUDActions.deleteGoogleClientCredentials(
                    facebookPageId,
                    record.id,
                  );
                  googleAdsCRUDActions.retrieveGoogleClientCredentials(
                    facebookPageId,
                  );
                }
              },
            });
          }}
          css={{ container: css.deleteBtn }}
        >
          <DeleteOutlined />
        </Button>
      ),
    },
  ];

  const handleSave = async () => {
    setBusy(true);

    try {
      const res = await googleAdsCRUDActions.createGoogleClientCredentials(
        facebookPageId,
        {
          clientId,
          clientSecret,
          customerId,
        },
      );

      if (res.success && res?.googleClientCredential?.redirectUrl) {
        window.location.href = res?.googleClientCredential?.redirectUrl;
        return;
      }

      if (res.errors) {
        errorToast(res.errors);
        return;
      }

      googleAdsCRUDActions.retrieveGoogleClientCredentials(facebookPageId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={css.content}>
      {!!credentials?.length && (
        <div className={css.card}>
          <div className={css.stepContentHeaderText}>
            <span className={css.stepContentTitle}>Accounts</span>
          </div>
          <ConfigProvider
            theme={{
              components: {
                Table: {
                  headerBg: 'white',
                  borderColor: '#D9D9D9',
                  borderRadius: 8,
                },
              },
            }}
          >
            <Table
              dataSource={credentials}
              columns={columns}
              bordered={false}
              pagination={false}
              size="small"
              className={css.table}
            />
          </ConfigProvider>
        </div>
      )}
      <div className={css.card}>
        <div className={css.stepContentHeaderText}>
          <span className={css.stepContentTitle}>Google Ads Setup</span>
          <span className={css.stepContentDescription}>
            Send data to Google Ads
          </span>
        </div>
        <div className={css.field}>
          <div className={css.fieldName}>
            <span>Client ID</span>
            <Tooltip
              title={
                <div>
                  Learn more about finding your Client ID
                  <a
                    className={css.learnMoreLink}
                    href="https://customers.ai/help/l/en/article/yk3wzsxkpz-finding-your-google-ads-client-id-secret-token-and-customer-id"
                    target="_blank"
                  >
                    Learn more
                  </a>
                </div>
              }
              placement="bottom"
            >
              <QuestionCircleOutlined className={css.infoIcon} />
            </Tooltip>
          </div>

          <Input
            onChange={e => setClientId(e.target.value.trim())}
            disabled={busy}
            value={clientId}
            type="text"
            placeholder="Add Client ID"
            css={{ container: css.input }}
          />
        </div>

        <div className={css.field}>
          <div className={css.fieldName}>
            <span>Secret Token</span>
            <Tooltip
              title={
                <div>
                  Learn more about finding your Secret Token
                  <a
                    className={css.learnMoreLink}
                    href="https://customers.ai/help/l/en/article/yk3wzsxkpz-finding-your-google-ads-client-id-secret-token-and-customer-id"
                    target="_blank"
                  >
                    Learn more
                  </a>
                </div>
              }
              placement="bottom"
            >
              <QuestionCircleOutlined className={css.infoIcon} />
            </Tooltip>
          </div>

          <Input
            onChange={e => setClientSecret(e.target.value.trim())}
            disabled={busy}
            value={clientSecret}
            placeholder="Add Secret Token"
            type="text"
            css={{ container: css.input }}
          />
        </div>

        <div className={css.field}>
          <div className={css.fieldContainer}>
            <div className={css.fieldName}>
              <span>Customer ID</span>
              <Tooltip title=" Customer ID" placement="bottom">
                <QuestionCircleOutlined className={css.infoIcon} />
              </Tooltip>
            </div>
            <a
              href=""
              className={classNames(css.whichVersionLink, css.hidden)}
              target="_blank"
            >
              <FileOutlined />
              Which version should I choose?
            </a>
          </div>
          <Input
            onChange={e => setCustomerId(e.target.value.trim())}
            disabled={busy}
            value={customerId}
            type="text"
            placeholder="Add Customer ID"
            css={{ container: css.input }}
          />
        </div>

        <Button
          css={{ container: css.submitBtn }}
          onClick={handleSave}
          disabled={busy || ![clientId, clientSecret, customerId].some(v => v)}
        >
          Save{' '}
          {busy && (
            <Spin
              indicator={
                <LoadingOutlined className={css.submitBtnSpinner} spin />
              }
            />
          )}
        </Button>
      </div>
    </div>
  );
};
export const SetupForm = ({ className }: SetupFormProps) => {
  const [configReady, setConfigFetchReady] = useState(false);
  const googleAdsCRUDActions = useGoogleAdsCRUDActions();
  const { hideSlideoutMenu } = useViewActions();

  const closeSlideout = async () => {
    hideSlideoutMenu();
  };

  const chatbotId = useSelector(activeChatbotIdSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const containerClassName = classNames(className, css.container);

  useEffect(() => {
    (async () => {
      try {
        await googleAdsCRUDActions.retrieveGoogleClientCredentials(
          facebookPageId,
        );
      } finally {
        setConfigFetchReady(true);
      }
    })();
  }, [chatbotId]);

  return (
    <div className={containerClassName}>
      <Button
        css={{ container: css.backBtn }}
        color="transparent"
        onClick={closeSlideout}
      >
        <ArrowLeftOutlined />
        Back to Google
      </Button>
      <div>{configReady && <AccountsStep />}</div>
    </div>
  );
};
